/**
 * ProfileCard - UI Profile Summary Card
 *
 * Displays a profile with its configuration summary and action buttons.
 */

import React from 'react';
import {
  Building2, UserCog, BarChart3, Edit2, Copy, Trash2, Star,
  LayoutGrid, Layers, ChevronRight, Box
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppFeatures } from '../../hooks/useAppFeatures.js';
import { MODULE_ICONS } from '../../constants/profile_constants.js';

// Icon mapping for profile icons
const PROFILE_ICONS = {
  Building2,
  UserCog,
  BarChart3,
};

// Nav style labels resolved at render via t('uiProfiles.form.navStyles.<code>.label') — piège #1

export function ProfileCard({
  profile,
  userCount = 0,
  onEdit,
  onDuplicate,
  onDelete,
  onSetDefault,
}) {
  const { t } = useTranslation();
  const ProfileIcon = PROFILE_ICONS[profile.icon] || UserCog;
  const modules = Array.isArray(profile.modules) ? profile.modules : [];
  const moduleCount = modules.length;
  const widgetCount = Array.isArray(profile.widgets) ? profile.widgets.length : 0;
  const { featuresByGroup, groups } = useAppFeatures();

  return (
    <div
      className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden hover:border-gray-300 dark:hover:border-slate-600 transition-colors shadow-sm"
      style={{ borderLeftColor: profile.color, borderLeftWidth: '4px' }}
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${profile.color}20` }}
            >
              <ProfileIcon size={20} style={{ color: profile.color }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 dark:text-white">{profile.name}</h3>
                {profile.is_default ? (
                  <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-500 dark:text-amber-400 text-xs rounded">
                    {t('uiProfiles.card.default')}
                  </span>
                ) : null}
                {profile.is_system ? (
                  <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-500 dark:text-purple-400 text-xs rounded">
                    {t('uiProfiles.card.system')}
                  </span>
                ) : null}
              </div>
              <p className="text-sm text-gray-500 dark:text-slate-400">{profile.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 pb-4">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 bg-gray-100 dark:bg-slate-700/50 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-slate-400">
              <Layers size={14} />
              <span className="text-lg font-bold text-gray-900 dark:text-white">{moduleCount}</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-slate-500">{t('uiProfiles.card.modules')}</div>
          </div>
          <div className="p-2 bg-gray-100 dark:bg-slate-700/50 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-slate-400">
              <LayoutGrid size={14} />
              <span className="text-lg font-bold text-gray-900 dark:text-white">{widgetCount}</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-slate-500">{t('uiProfiles.card.widgets')}</div>
          </div>
          <div className="p-2 bg-gray-100 dark:bg-slate-700/50 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-slate-400">
              <UserCog size={14} />
              <span className="text-lg font-bold text-gray-900 dark:text-white">{userCount}</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-slate-500">{t('uiProfiles.card.users')}</div>
          </div>
        </div>
      </div>

      {/* Mini sidebar preview strip */}
      {moduleCount > 0 && (
        <div className="px-4 pb-3">
          <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1.5">{t('uiProfiles.card.sidebar')}</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {Object.entries(groups)
              .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
              .map(([groupCode, group]) => {
                const groupFeatures = (featuresByGroup[groupCode] || []).filter(f => modules.includes(f.id));
                if (groupFeatures.length === 0) return null;
                return (
                  <div key={groupCode} className="flex items-center gap-1">
                    <span className="text-[10px] text-gray-400 dark:text-slate-500">{group.label}:</span>
                    <div className="flex gap-0.5">
                      {groupFeatures.slice(0, 4).map(f => {
                        const Icon = MODULE_ICONS[f.id] || Box;
                        return <Icon key={f.id} size={12} className="text-gray-500 dark:text-slate-400" title={f.label} />;
                      })}
                      {groupFeatures.length > 4 && (
                        <span className="text-[10px] text-gray-400 dark:text-slate-500">+{groupFeatures.length - 4}</span>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Meta info */}
      <div className="px-4 pb-3">
        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-slate-500">
          <span className="flex items-center gap-1">
            <ChevronRight size={12} />
            {t('uiProfiles.card.nav', { style: t(`uiProfiles.form.navStyles.${profile.nav_style}.label`, { defaultValue: profile.nav_style }) })}
          </span>
          <span className="flex items-center gap-1">
            {t('uiProfiles.card.code')} <code className="text-gray-600 dark:text-slate-400">{profile.code}</code>
          </span>
        </div>
      </div>

      {/* Status & Actions */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between">
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            profile.status === 'active'
              ? 'bg-green-500/20 text-green-500 dark:text-green-400'
              : 'bg-red-500/20 text-red-500 dark:text-red-400'
          }`}
        >
          {profile.status === 'active' ? t('uiProfiles.card.active') : t('uiProfiles.card.inactive')}
        </span>

        <div className="flex items-center gap-1">
          {!profile.is_default && (
            <button
              onClick={onSetDefault}
              className="p-2 rounded-lg text-gray-400 dark:text-slate-400 hover:text-amber-500 dark:hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
              title={t('uiProfiles.card.setDefault')}
            >
              <Star size={16} />
            </button>
          )}
          <button
            onClick={onDuplicate}
            className="p-2 rounded-lg text-gray-400 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
            title={t('uiProfiles.card.duplicate')}
          >
            <Copy size={16} />
          </button>
          <button
            onClick={onEdit}
            className="p-2 rounded-lg text-gray-400 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            title={t('common.actions.edit')}
          >
            <Edit2 size={16} />
          </button>
          {!profile.is_system && (
            <button
              onClick={onDelete}
              className="p-2 rounded-lg text-gray-400 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-500/10 transition-colors"
              title={t('common.actions.delete')}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileCard;
